import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useMemo } from 'react';
import { useDevice } from '../../../providers/Device';
import { getTestIds } from '../../../utils/TestIds';
import { emptyColumnsState } from '../tableCore/columns/columnsState';
import { useColumnFloors } from '../tableCore/columns/useColumnFloors';
import { columnsByName, toMobileCardFields } from '../tableCore/mobileCard/mobileCard';
import { STATIC_EDGE_PADDING } from '../tableCore/table.constants';
import styles from '../tableCore/table.module.scss';
import { TableHeader } from '../tableCore/TableHeader';
import { TableMobileCards } from '../tableCore/TableMobileCards/TableMobileCards';
import { TableRow } from '../tableCore/TableRow';
import { TableSurface } from '../tableCore/TableSurface/TableSurface';
import { useTableLayout } from '../tableCore/useTableLayout/useTableLayout';
import { renderRowAction, toRenderRows } from './StaticTable.utils';
import { StaticTableSummaryRow } from './StaticTableSummaryRow/StaticTableSummaryRow';
import { SummaryMobileRows } from './SummaryMobileRows/SummaryMobileRows';
export function StaticTable(param) {
    let { mainColumn, columns, items, getRowId, summaryRows, mobile, rowAction, banner, footer, emptyState, isRowEntering, now, testId } = param;
    const testIds = useMemo(()=>getTestIds(testId), [
        testId
    ]);
    const { isMobileDevice } = useDevice();
    const columnsState = useMemo(()=>emptyColumnsState(), []);
    const { floors, measurer } = useColumnFloors({
        mainColumn,
        columns,
        headerLabelVariant: 'paragraph2'
    });
    const { scrollerRef, columns: resolved, minWidth } = useTableLayout({
        mainColumn,
        columns,
        columnsState,
        floors,
        hasActions: rowAction != null,
        reservedWidth: STATIC_EDGE_PADDING,
        padsEndEdge: false,
        framed: true
    });
    const renderRows = useMemo(()=>toRenderRows({
            items,
            getRowId
        }), [
        items,
        getRowId
    ]);
    const byName = useMemo(()=>columnsByName(mainColumn != null ? [
            mainColumn,
            ...columns
        ] : columns), [
        mainColumn,
        columns
    ]);
    const badgeColumn = useMemo(()=>mobile.badgeField == null ? undefined : byName.get(String(mobile.badgeField)), [
        byName,
        mobile.badgeField
    ]);
    const mobileCardFields = useMemo(()=>toMobileCardFields({
            byName,
            mobile: {
                fields: mobile.fields
            }
        }), [
        byName,
        mobile
    ]);
    const mobileFooter = footer == null ? null : /*#__PURE__*/ _jsx("div", {
        "data-testid": testIds.footer(),
        children: footer
    });
    if (isMobileDevice) {
        return /*#__PURE__*/ _jsxs("div", {
            className: styles.mobileSummaryFrame,
            children: [
                banner != null && /*#__PURE__*/ _jsx("div", {
                    className: styles.mobileBanner,
                    "data-testid": testIds.banner(),
                    children: banner
                }),
                items.length === 0 && emptyState != null ? /*#__PURE__*/ _jsxs(_Fragment, {
                    children: [
                        /*#__PURE__*/ _jsx("div", {
                            "data-testid": testIds.emptyState(),
                            children: emptyState
                        }),
                        mobileFooter
                    ]
                }) : /*#__PURE__*/ _jsxs(TableMobileCards, {
                    items: items,
                    getRowId: getRowId,
                    mainColumn: mainColumn,
                    fields: mobileCardFields,
                    badgeColumn: badgeColumn,
                    actionSlot: (row, index)=>{
                        var _ref;
                        return renderRowAction({
                            action: rowAction === null || rowAction === void 0 ? void 0 : rowAction(row),
                            withLabel: true,
                            testId: testIds.action((_ref = getRowId === null || getRowId === void 0 ? void 0 : getRowId(row, index)) !== null && _ref !== void 0 ? _ref : index)
                        });
                    },
                    gray: true,
                    now: now,
                    testId: testId,
                    children: [
                        mobileFooter,
                        /*#__PURE__*/ _jsx(SummaryMobileRows, {
                            summaryRows: summaryRows,
                            testId: testId
                        })
                    ]
                })
            ]
        });
    }
    return /*#__PURE__*/ _jsx(TableSurface, {
        scrollerRef: scrollerRef,
        framed: true,
        interactive: false,
        scrollable: false,
        testId: testId,
        children: /*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                measurer,
                banner != null && /*#__PURE__*/ _jsx("div", {
                    className: styles.banner,
                    style: {
                        minWidth
                    },
                    "data-testid": testIds.banner(),
                    children: banner
                }),
                /*#__PURE__*/ _jsx(TableHeader, {
                    columns: resolved,
                    minWidth: minWidth,
                    labelVariant: "paragraph2",
                    testId: testIds.header()
                }),
                items.length === 0 && emptyState != null ? /*#__PURE__*/ _jsx("div", {
                    className: styles.emptyState,
                    style: {
                        minWidth
                    },
                    "data-testid": testIds.emptyState(),
                    children: emptyState
                }) : /*#__PURE__*/ _jsx("div", {
                    className: styles.rowsSection,
                    style: {
                        minWidth
                    },
                    children: renderRows.map((param)=>{
                        let { row, rowId, nested } = param;
                        return /*#__PURE__*/ _jsx(TableRow, {
                            row: row,
                            rowId: rowId,
                            columns: resolved,
                            actionSlot: nested ? undefined : renderRowAction({
                                action: rowAction === null || rowAction === void 0 ? void 0 : rowAction(row),
                                withLabel: false,
                                testId: testIds.action(rowId)
                            }),
                            minWidth: minWidth,
                            rowStyle: "summary",
                            nested: nested,
                            entering: (isRowEntering === null || isRowEntering === void 0 ? void 0 : isRowEntering(row)) === true,
                            now: now,
                            testId: testIds.row(rowId)
                        }, rowId);
                    })
                }),
                summaryRows.length > 0 && /*#__PURE__*/ _jsx("div", {
                    className: styles.summarySection,
                    style: {
                        minWidth
                    },
                    "data-testid": testIds.summarySection(),
                    children: summaryRows.map((summary)=>/*#__PURE__*/ _jsx(StaticTableSummaryRow, {
                            summary: summary,
                            columns: resolved,
                            minWidth: minWidth,
                            testId: testId
                        }, summary.id))
                }),
                footer != null && /*#__PURE__*/ _jsx("div", {
                    className: styles.footer,
                    style: {
                        minWidth
                    },
                    "data-testid": testIds.footer(),
                    children: footer
                })
            ]
        })
    });
}
